import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { contestsAPI } from '../services/api';
import Toast from '../components/Toast';
import { Plus, Users, Lock, Swords, Copy, Check, Globe } from 'lucide-react';
import { formatNumber } from '../utils/format';

const TYPE_ICONS = { private: Lock, public: Globe, '1v1': Swords };
const TYPE_LABELS = { private: 'Private', public: 'Public', '1v1': '1v1' };
const TYPE_COLORS = { private: 'var(--blue)', public: 'var(--green)', '1v1': 'var(--accent)' };

function ContestCard({ contest, onJoin, joined }) {
  const navigate = useNavigate();
  const Icon = TYPE_ICONS[contest.type] || Users;
  const color = TYPE_COLORS[contest.type] || 'var(--text-secondary)';
  const count = parseInt(contest.participant_count || 0);
  const max = parseInt(contest.max_participants || 0);
  const full = max > 0 && count >= max;

  const [copied, setCopied] = useState(false);
  const copyCode = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(contest.invite_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div
      className="card"
      style={{ cursor: 'pointer', transition: 'border-color 0.15s' }}
      onClick={() => joined ? navigate(`/contests/${contest.id}`) : undefined}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: color + '20', flexShrink: 0,
          }}>
            <Icon size={15} color={color} />
          </span>
          <div>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{contest.name}</div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 1 }}>
              by {contest.creator_name} · {TYPE_LABELS[contest.type]}
            </div>
          </div>
        </div>
        <span style={{
          fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 20,
          background: contest.status === 'completed' ? 'var(--bg-secondary)' : 'rgba(16,185,129,0.1)',
          color: contest.status === 'completed' ? 'var(--text-muted)' : 'var(--green)',
        }}>
          {contest.status === 'completed' ? 'Ended' : full ? 'Full' : 'Open'}
        </span>
      </div>

      {contest.description && (
        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 10, lineHeight: 1.5 }}>
          {contest.description}
        </p>
      )}

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <span className="stat-chip"><Users size={11} /> <strong>{formatNumber(count)}</strong>{max > 0 ? `/${formatNumber(max)}` : ''} players</span>
        <span className="stat-chip">⬡ <strong>{contest.start_points}</strong> pts start</span>
        <span className="stat-chip">🔁 <strong>{contest.max_trades}</strong> trades</span>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        {contest.invite_code && (
          <button
            onClick={copyCode}
            style={{
              background: 'var(--bg-secondary)', border: '1px solid var(--border)',
              borderRadius: 6, padding: '5px 10px', fontSize: '0.75rem',
              color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
              fontFamily: 'var(--font-mono)',
            }}
          >
            {copied ? <Check size={11} /> : <Copy size={11} />}
            {contest.invite_code}
          </button>
        )}
        {joined ? (
          <button className="btn btn-outline" style={{ flex: 1, padding: '7px' }} onClick={() => navigate(`/contests/${contest.id}`)}>
            Open
          </button>
        ) : (
          <button
            className="btn btn-buy"
            style={{ flex: 1, padding: '7px', opacity: full ? 0.5 : 1 }}
            disabled={full || contest.already_joined}
            onClick={(e) => { e.stopPropagation(); onJoin(contest.invite_code); }}
          >
            {contest.already_joined ? 'Joined' : full ? 'Full' : 'Join'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function ContestsPage() {
  const [myContests, setMyContests]     = useState([]);
  const [publicContests, setPublicContests] = useState([]);
  const [tab, setTab]   = useState('my');
  const [loading, setLoading] = useState(true);
  const [toast, setToast]     = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [joinCode, setJoinCode]     = useState('');
  const [form, setForm] = useState({
    name: '', description: '', type: 'private',
    start_points: 2000, max_trades: 200, max_participants: 0,
  });
  const navigate = useNavigate();

  const fetchAll = useCallback(async () => {
    try {
      const myRes = await contestsAPI.my();
      setMyContests(myRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const r = await contestsAPI.create(form);
      setToast({ msg: r.data.message, type: 'success' });
      setShowCreate(false);
      fetchAll();
      navigate(`/contests/${r.data.contest.id}`);
    } catch (err) {
      setToast({ msg: err.response?.data?.error || 'Create failed', type: 'error' });
    }
  };

  const handleJoin = async (code) => {
    try {
      const r = await contestsAPI.join(code || joinCode);
      setToast({ msg: r.data.message, type: 'success' });
      setJoinCode('');
      fetchAll();
      navigate(`/contests/${r.data.contest.id}`);
    } catch (err) {
      setToast({ msg: err.response?.data?.error || 'Join failed', type: 'error' });
    }
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onDismiss={() => setToast(null)} />}

      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h1 className="page-title">Contests</h1>
          <p className="page-subtitle">Private groups, office pools, 1v1 battles — separate team per contest</p>
        </div>
        <button className="btn btn-buy" style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => setShowCreate(true)}>
          <Plus size={15} /> Create Contest
        </button>
      </div>

      {/* Join by code */}
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)', padding: '12px 14px',
        display: 'flex', gap: 8, marginBottom: '1.5rem', flexWrap: 'wrap',
      }}>
        <input
          value={joinCode}
          onChange={e => setJoinCode(e.target.value.toUpperCase())}
          placeholder="Enter invite code (e.g. AB3K7P)"
          style={{
            flex: 1, minWidth: 160,
            background: 'var(--bg-secondary)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)', padding: '8px 12px',
            color: 'var(--text-primary)', fontSize: '0.88rem', fontFamily: 'var(--font-mono)',
            letterSpacing: 3, outline: 'none',
          }}
          maxLength={6}
        />
        <button className="btn btn-buy" style={{ padding: '8px 20px' }} onClick={() => handleJoin()} disabled={joinCode.length < 4}>
          Join
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: '1.25rem' }}>
        <button className={`filter-chip${tab === 'my' ? ' active' : ''}`} onClick={() => setTab('my')}>
          My Contests ({myContests.length})
        </button>

      </div>

      {/* Contest list */}
      {tab === 'my' && (
        myContests.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
            <Users size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
            <p>No contests yet. Create one or join with an invite code.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
            {myContests.map(c => <ContestCard key={c.id} contest={c} joined onJoin={handleJoin} />)}
          </div>
        )
      )}



      {/* Create contest modal */}
      {showCreate && (
        <div
          onClick={() => setShowCreate(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: '1.5rem', width: '100%', maxWidth: 440 }}
          >
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', marginBottom: '1.25rem' }}>Create Contest</h2>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label className="form-label">Contest name</label>
                <input className="form-input" value={form.name} onChange={set('name')} placeholder="Office IPL Pool 2026" required />
              </div>
              <div className="form-group">
                <label className="form-label">Description (optional)</label>
                <input className="form-input" value={form.description} onChange={set('description')} placeholder="Office fantasy trading for IPL season" />
              </div>
              <div className="form-group">
                <label className="form-label">Type</label>
                <select className="form-input" value={form.type} onChange={set('type')}>
                  <option value="private">Private (invite code only)</option>
                  <option value="1v1">1v1 (head to head)</option>
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="form-group">
                  <label className="form-label">Starting points</label>
                  <input className="form-input" type="number" value={form.start_points} onChange={set('start_points')} min={100} max={10000} />
                </div>
                <div className="form-group">
                  <label className="form-label">Max trades</label>
                  <input className="form-input" type="number" value={form.max_trades} onChange={set('max_trades')} min={10} max={500} />
                </div>
              </div>
              {form.type !== '1v1' && (
                <div className="form-group">
                  <label className="form-label">Max participants (0 = unlimited)</label>
                  <input className="form-input" type="number" value={form.max_participants} onChange={set('max_participants')} min={0} />
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn btn-buy" style={{ flex: 2 }}>Create &amp; Get Invite Code</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
